import { applyJsonPatch } from './patch';
import { type RootState } from '../core/state-shape';

// Import all stores that can be patched
import { useTasksStore } from '@/stores/tasks';
import { useProjectsStore } from '@/stores/projects';
import { useValidatorsStore } from '@/stores/validators';
import { useReviewStore } from '@/stores/review';
import { useContextBundlesStore } from '@/stores/contextBundles';
import { useFilesStore } from '@/stores/files';
import { useIndexStore } from '@/stores/indexing';
import { useNotificationsStore } from '@/stores/notifications';
import { useProposalsStore } from '@/stores/proposals';
import { useSettingsStore } from '@/stores/settings';
import { useMobileStore } from '@/stores/mobile';
import { useAppStatusStore } from '@/stores/appStatus';

type PatchOp = { op: 'add' | 'replace' | 'remove'; path: string; value?: any };

function applyState(state: RootState) {
    // Apply the initial state to all stores
    if (state.tasks) useTasksStore.setState(state.tasks);
    if (state.projects) useProjectsStore.setState(state.projects);
    if (state.validators) useValidatorsStore.setState(state.validators);
    if (state.review) useReviewStore.setState(state.review);
    if (state.contextBundles) useContextBundlesStore.setState(state.contextBundles);
    if (state.files) useFilesStore.setState(state.files);
    if (state.index) useIndexStore.setState(state.index);
    if (state.notifications) useNotificationsStore.setState(state.notifications);
    if (state.proposals) useProposalsStore.setState(state.proposals);
    
    // For settings, merge with local UI preferences to avoid overwriting them.
    if (state.settings) {
        const localSettings = useSettingsStore.getState();
        const mergedSettings = {
            ...state.settings,
            appearance: localSettings.appearance,
            llmProviders: (state as any).llmProviders || {},
        };
        useSettingsStore.setState(mergedSettings);
    }
    
    if (state.mobileSessions) useMobileStore.setState(state.mobileSessions);
    if (state.toolAvailability) useAppStatusStore.getState().patch({ toolAvailability: state.toolAvailability });
}

function applyPatches(patches: PatchOp[]) {
    // This is the central patch application logic.
    // It calls the `setState` method of each root-level store.
    for (const op of patches) {
        const rootKey = op.path.split('/')[1] as keyof RootState;

        switch (rootKey) {
            case 'tasks':
                useTasksStore.setState(
                    (state) => applyJsonPatch(state, { ...op, path: op.path.substring('/tasks'.length) })
                );
                break;
            case 'projects':
                useProjectsStore.setState(
                    (state) => applyJsonPatch(state, { ...op, path: op.path.substring('/projects'.length) })
                );
                break;
            case 'validators':
                useValidatorsStore.setState(
                    (state) => applyJsonPatch(state, { ...op, path: op.path.substring('/validators'.length) })
                );
                break;
            case 'review':
                useReviewStore.setState(
                    (state) => applyJsonPatch(state, { ...op, path: op.path.substring('/review'.length) })
                );
                break;
            case 'contextBundles':
                useContextBundlesStore.setState(
                    (state) => applyJsonPatch(state, { ...op, path: op.path.substring('/contextBundles'.length) })
                );
                break;
            case 'files':
                useFilesStore.setState(
                    (state) => applyJsonPatch(state, { ...op, path: op.path.substring('/files'.length) })
                );
                break;
            case 'index':
                useIndexStore.setState(
                    (state) => applyJsonPatch(state, { ...op, path: op.path.substring('/index'.length) })
                );
                break;
            case 'notifications':
                useNotificationsStore.setState(
                    (state) => applyJsonPatch(state, { ...op, path: op.path.substring('/notifications'.length) })
                );
                break;
            case 'proposals':
                useProposalsStore.setState(
                    (state) => applyJsonPatch(state, { ...op, path: op.path.substring('/proposals'.length) })
                );
                break;
            case 'settings':
                useSettingsStore.setState(
                    (state) => applyJsonPatch(state, { ...op, path: op.path.substring('/settings'.length) })
                );
                break;
            case 'mobileSessions':
                useMobileStore.setState(
                    (state) => applyJsonPatch(state, { ...op, path: op.path.substring('/mobileSessions'.length) })
                );
                break;
            default:
                console.warn(`No store configured to handle patch for path: ${op.path}`);
        }
    }
}


export function initializePatcher() {
    window.addEventListener('apply-state', (event: Event) => {
        const customEvent = event as CustomEvent<RootState>;
        applyState(customEvent.detail);
    });

    window.addEventListener('apply-patches', (event: Event) => {
        const customEvent = event as CustomEvent<PatchOp[]>;
        applyPatches(customEvent.detail);
    });
}